import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { Check, LoaderCircle, Mail } from 'lucide-react';

import {
  EMAIL_ASK_ALSO_TIPS,
  EMAIL_ASK_EMAIL,
  EMAIL_ASK_FIRST_NAME,
  EMAIL_ASK_TURN_OFF,
  EMAIL_ASK_WAITING_TAIL,
  emailAskAddress,
  emailAskLimits,
  emailAskWords,
  type EmailAskInput,
  type EmailAskKind,
  type EmailAskState,
  type EmailAsksView,
} from '../shared/email-asks';
import { TECHNICAL_DETAILS_MARKER } from '../shared/error-details';
import { OperatorErrorText } from './TechnicalDetails';
import { reportEvent } from './telemetry';
import './email-ask.css';

/**
 * The email asks as main holds them (shared/email-asks.ts), kept current. Null until read, and
 * while the switch is off the view says `available: false`: no ask is shown.
 */
export function useEmailAsks(): EmailAsksView | null {
  const [view, setView] = useState<EmailAsksView | null>(null);
  useEffect(() => {
    const api = window.capturePointDesktop;
    if (!api?.getEmailAsks) return;
    let live = true;
    void api
      .getEmailAsks()
      .then((next) => {
        if (live) setView(next);
      })
      .catch(() => undefined);
    const stop = api.onEmailAsksChanged?.((next) => {
      if (live) setView(next);
    });
    return () => {
      live = false;
      stop?.();
    };
  }, []);
  return view;
}

/**
 * One email ask, the same in every place (mockup, "The same in all four"): a quiet underlined line
 * with a small envelope; after the click, in its place, one row of a small "First name (optional)"
 * box, the email box and a short button that says what happens, wrapping at narrow widths, with
 * "Also send me a few tips" under it, unticked, only while this computer isn't getting tips; once
 * done, where it went with Turn off beside the address. No pop-up, banner or hint.
 */
export function EmailAsk({
  kind,
  view,
  done,
  details,
  jobId,
  className = '',
}: {
  kind: EmailAskKind;
  view: EmailAsksView;
  /** This place's ask as main holds it (a job's note, this test's report, today's sheet); null when off. */
  done: EmailAskState | null;
  /** What goes with the email: a learning note's job and its picture, the speed report's facts. Read at Send. */
  details?: () => Pick<EmailAskInput, 'jobId' | 'jobIcon' | 'report'>;
  /** The learning note's job, for Turn off. */
  jobId?: string;
  className?: string;
}) {
  const words = emailAskWords[kind];
  const [stage, setStage] = useState<'line' | 'row'>('line');
  const [firstName, setFirstName] = useState('');
  const [email, setEmail] = useState('');
  const [alsoTips, setAlsoTips] = useState(false);
  const [busy, setBusy] = useState<'send' | 'off' | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  // What Send answered, until main's own view says the same (or Turn off clears it).
  const [sent, setSent] = useState<EmailAskState | null>(null);
  const firstRef = useRef<HTMLInputElement | null>(null);
  const id = useId();
  // PAIGE (paige/server-email-asks.ts): every ask mails the address the person signs in with, so the box shows it and
  // cannot be changed, as the tips form's (MoreTipsAsk.tsx).
  const signInAddress = window.paigeWorkbench ? view.prefill.email || null : null;
  // While Send is out, the row stays (with Sending…) whatever main reports in the meantime.
  const shown = busy === 'send' ? null : (done ?? sent);

  useEffect(() => {
    if (stage === 'row') firstRef.current?.focus();
  }, [stage]);
  // Main's view caught up: it is the one answer from here on.
  useEffect(() => {
    if (done) setSent(null);
  }, [done]);

  const open = () => {
    setFirstName((current) => current || view.prefill.firstName);
    setEmail((current) => current || view.prefill.email);
    setAlsoTips(false);
    setProblem(null);
    setStage('row');
    reportEvent(`email-ask.opened:${kind}`);
  };

  const send = async (event: FormEvent) => {
    event.preventDefault();
    const api = window.capturePointDesktop;
    if (!api?.sendEmailAsk || busy) return;
    const address = emailAskAddress(email);
    if (!address) {
      setProblem('Check the email address.');
      return;
    }
    setBusy('send');
    setProblem(null);
    try {
      const answer = await api.sendEmailAsk({
        kind,
        email: address,
        ...(firstName.trim() ? { firstName: firstName.trim() } : {}),
        ...(alsoTips && !view.tipsOn ? { alsoTips: true } : {}),
        ...(details?.() ?? {}),
      });
      if (answer.outcome === 'failed') setProblem(answer.message);
      else {
        setSent({ email: address, waiting: answer.outcome === 'waiting' });
        setStage('line');
      }
    } catch (reason) {
      setProblem(sendFailure(reason));
    } finally {
      setBusy(null);
    }
  };

  const turnOff = async () => {
    const api = window.capturePointDesktop;
    if (!api?.turnOffEmailAsk || busy) return;
    setBusy('off');
    setProblem(null);
    try {
      await api.turnOffEmailAsk(kind === 'learning-note' ? { kind, jobId } : { kind });
      setSent(null);
      setStage('line');
    } catch (reason) {
      setProblem(sendFailure(reason));
    } finally {
      setBusy(null);
    }
  };

  if (shown) {
    const waiting = shown.waiting && words.waiting !== null;
    return (
      <div className={`email-ask email-ask-${kind} ${className}`}>
        <p className="email-ask-done" role="status">
          <Check size={15} aria-hidden="true" />
          <span>
            {waiting ? words.waiting : words.done} <b>{shown.email}</b>
            {waiting ? ` ${EMAIL_ASK_WAITING_TAIL}` : ''}
          </span>
          <button
            type="button"
            className="email-ask-off"
            data-telemetry={`email-ask-off-${kind}`}
            disabled={busy !== null}
            onClick={() => void turnOff()}
          >
            {EMAIL_ASK_TURN_OFF}
          </button>
        </p>
        {problem && (
          <div className="email-ask-problem" role="alert" data-telemetry="error:email-ask-failed">
            <OperatorErrorText message={problem} />
          </div>
        )}
      </div>
    );
  }

  if (stage === 'line')
    return (
      <div className={`email-ask email-ask-${kind} ${className}`}>
        <button
          type="button"
          className="email-ask-line"
          data-telemetry={`email-ask-${kind}`}
          onClick={open}
        >
          <Mail size={15} aria-hidden="true" />
          {words.line}
        </button>
      </div>
    );

  return (
    <form
      className={`email-ask email-ask-${kind} is-open ${className}`}
      onSubmit={(event) => void send(event)}
      noValidate
    >
      <div className="email-ask-row">
        <input
          ref={firstRef}
          className="email-ask-input email-ask-first"
          value={firstName}
          onChange={(event) => {
            setFirstName(event.target.value);
            setProblem(null);
          }}
          aria-label={EMAIL_ASK_FIRST_NAME}
          placeholder={EMAIL_ASK_FIRST_NAME}
          autoComplete="given-name"
          maxLength={emailAskLimits.firstName}
          disabled={busy !== null}
        />
        <input
          className="email-ask-input email-ask-email"
          type="email"
          value={email}
          onChange={(event) => {
            setEmail(event.target.value);
            setProblem(null);
          }}
          aria-label="Email"
          placeholder={EMAIL_ASK_EMAIL}
          autoComplete="email"
          maxLength={emailAskLimits.email}
          disabled={busy !== null}
          readOnly={Boolean(signInAddress)}
          title={signInAddress ? 'Paige emails the address you sign in with.' : undefined}
          aria-invalid={problem === 'Check the email address.' || undefined}
          aria-describedby={problem ? `${id}-problem` : undefined}
        />
        {/* Before the button in the page, so Tab reaches it first; on its own line under the row. */}
        {!view.tipsOn && (
          <label className="email-ask-tips">
            <input
              type="checkbox"
              checked={alsoTips}
              onChange={(event) => setAlsoTips(event.target.checked)}
              disabled={busy !== null}
              data-telemetry={`email-ask-tips-${kind}`}
            />
            {EMAIL_ASK_ALSO_TIPS}
          </label>
        )}
        <button
          type="submit"
          className="secondary-button small email-ask-send"
          data-telemetry={`email-ask-send-${kind}`}
          disabled={busy !== null}
        >
          {busy === 'send' ? (
            <>
              <LoaderCircle size={14} className="spin" aria-hidden="true" />
              Sending…
            </>
          ) : (
            words.button
          )}
        </button>
      </div>
      {problem && (
        <div
          id={`${id}-problem`}
          className="email-ask-problem"
          role="alert"
          data-telemetry="error:email-ask-failed"
        >
          <OperatorErrorText message={problem} />
        </div>
      )}
    </form>
  );
}

/** Main's own refusal of what was typed reads as it is; anything else says try again, with the exact error. */
function sendFailure(reason: unknown): string {
  const raw = reason instanceof Error ? reason.message : '';
  const message = raw.replace(/^[\s\S]*Error: /, '').trim();
  if (message === 'Enter a valid email address.' || message.startsWith('Keep the first name'))
    return message;
  return `That didn’t go through. Try again.${TECHNICAL_DETAILS_MARKER}${raw || 'No answer from Paige.'}`;
}
